// Rummy previous-game result popup. Shown on lobby entry when the
// server has an unacknowledged `rummy_last_game_result` row for the
// user — i.e. the user's previous match finalised while they were
// offline / had refreshed / closed the tab. Once the user taps
// "Back to Lobby" we POST /api/rummy/acknowledge-result so the
// popup never shows again (plus a localStorage guard in
// core/lib/recoveryAck.ts as a network-failure safety net).
//
// Title is split into a small "PREVIOUS GAME" caption above the big
// outcome word so narrow devices don't wrap the phrase mid-word —
// same fix applied to Ludo's RecoveryScreen.

export type RummyRecoveryKind =
  | 'win'
  | 'loss'
  | 'drop_first'
  | 'drop_middle'
  | 'kick'
  | 'invalid_declare';

type Props = {
  kind: RummyRecoveryKind;
  /** Credited payout (win) OR penalty-point score (loss-ish). */
  amount: number;
  pot: number;
  platformFee?: number;
  opponents?: Array<{ name?: string }>;
  onAck: () => void;
};

function fmt(n: number) {
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export default function RecoveryScreen({
  kind, amount, pot: _pot, platformFee, opponents, onAck,
}: Props) {
  const isWin = kind === 'win';
  const iconSrc = isWin
    ? '/games/rummy/Victory_Trophy.png'
    : '/games/rummy/Defeat_Medal.png';

  const titleLabel = 'PREVIOUS GAME';
  const title = (() => {
    switch (kind) {
      case 'win':             return 'YOU WON!';
      case 'loss':            return 'YOU LOST';
      case 'drop_first':
      case 'drop_middle':     return 'DROPPED';
      case 'kick':            return 'KICKED';
      case 'invalid_declare': return 'INVALID DECLARE';
    }
  })();

  const amountLabel = (() => {
    switch (kind) {
      case 'win':             return 'Credited';
      case 'loss':            return 'Penalty Points';
      case 'drop_first':      return 'First-drop Penalty';
      case 'drop_middle':     return 'Middle-drop Penalty';
      case 'kick':            return 'Kick Penalty';
      case 'invalid_declare': return 'Invalid-declare Penalty';
    }
  })();

  const oppNames = (opponents || []).map((o) => o?.name).filter(Boolean).join(', ');
  const subtitle = oppNames
    ? `Played vs ${oppNames}`
    : isWin
      ? 'Glory was yours'
      : 'Better luck next round';

  return (
    <div className="rummy-modal-backdrop">
      <div className="rummy-modal" style={{ maxWidth: 360 }}>
        <img
          src={iconSrc}
          alt=""
          className="mx-auto mb-2"
          style={{ width: 80, height: 80, objectFit: 'contain' }}
        />
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.32em',
            color: 'rgba(223, 255, 122, 0.85)',
            textTransform: 'uppercase',
            marginBottom: 2,
          }}
        >
          {titleLabel}
        </div>
        <h2 style={{ marginTop: 0, marginBottom: 6 }}>{title}</h2>
        <p
          style={{
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#a7f3d0',
            marginBottom: 10,
          }}
        >
          {subtitle}
        </p>
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%',
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid #bc9648',
            borderRadius: 12,
            padding: '10px 14px',
            marginBottom: 14,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#dfff7a',
            }}
          >
            {amountLabel}
          </span>
          <span
            style={{
              fontSize: 22, fontWeight: 900,
              color: '#fde08b', fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmt(amount)}
          </span>
        </div>
        {isWin && platformFee ? (
          <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 10 }}>
            Platform fee: {fmt(platformFee)}
          </div>
        ) : null}
        <button
          className="px-6 py-2 rounded-full bg-emerald-700 text-white font-bold"
          onClick={onAck}
        >
          Back to Lobby
        </button>
      </div>
    </div>
  );
}
